import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, MousePointer, Eye, TrendingUp, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface EmailLog {
  id: string;
  email_type: string;
  sent_at: string;
  status: string;
  opened_at: string | null;
  clicked_at: string | null;
  open_count: number;
  click_count: number;
}

interface AnalyticsSummary {
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  openRate: number;
  clickRate: number;
  clickToOpenRate: number;
}

const tierLabels: Record<string, { label: string; color: string }> = {
  "90_day": { label: "90 Day", color: "bg-blue-100 text-blue-800" },
  "120_day": { label: "120 Day", color: "bg-amber-100 text-amber-800" },
  "150_day": { label: "150 Day", color: "bg-red-100 text-red-800" },
};

export function EmailAnalytics() {
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30");
  const [summary, setSummary] = useState<AnalyticsSummary>({
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    openRate: 0,
    clickRate: 0,
    clickToOpenRate: 0,
  });
  const [tierBreakdown, setTierBreakdown] = useState<Record<string, AnalyticsSummary>>({});

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const { data, error } = await supabase
        .from("reengagement_email_log")
        .select("*")
        .gte("sent_at", startDate.toISOString())
        .order("sent_at", { ascending: false });

      if (error) throw error;

      const typedData = (data || []) as EmailLog[];
      setLogs(typedData);

      // Calculate overall summary
      const totalSent = typedData.filter(l => l.status === "sent").length;
      const totalOpened = typedData.filter(l => l.opened_at).length;
      const totalClicked = typedData.filter(l => l.clicked_at).length;

      setSummary({
        totalSent,
        totalOpened,
        totalClicked,
        openRate: totalSent > 0 ? (totalOpened / totalSent) * 100 : 0,
        clickRate: totalSent > 0 ? (totalClicked / totalSent) * 100 : 0,
        clickToOpenRate: totalOpened > 0 ? (totalClicked / totalOpened) * 100 : 0,
      });

      // Calculate tier breakdown
      const breakdown: Record<string, AnalyticsSummary> = {};
      for (const tier of ["90_day", "120_day", "150_day"]) {
        const tierLogs = typedData.filter(l => l.email_type === tier);
        const sent = tierLogs.filter(l => l.status === "sent").length;
        const opened = tierLogs.filter(l => l.opened_at).length;
        const clicked = tierLogs.filter(l => l.clicked_at).length;

        breakdown[tier] = {
          totalSent: sent,
          totalOpened: opened,
          totalClicked: clicked,
          openRate: sent > 0 ? (opened / sent) * 100 : 0,
          clickRate: sent > 0 ? (clicked / sent) * 100 : 0,
          clickToOpenRate: opened > 0 ? (clicked / opened) * 100 : 0,
        };
      }
      setTierBreakdown(breakdown);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Re-engagement Campaign Analytics</h3>
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-40">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Mail className="h-4 w-4" />
              <span className="text-xs">Sent</span>
            </div>
            <p className="text-2xl font-bold">{summary.totalSent}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Eye className="h-4 w-4" />
              <span className="text-xs">Opened</span>
            </div>
            <p className="text-2xl font-bold">{summary.totalOpened}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <MousePointer className="h-4 w-4" />
              <span className="text-xs">Clicked</span>
            </div>
            <p className="text-2xl font-bold">{summary.totalClicked}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs">Open Rate</span>
            </div>
            <p className="text-2xl font-bold">{summary.openRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs">Click Rate</span>
            </div>
            <p className="text-2xl font-bold">{summary.clickRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs">Click-to-Open</span>
            </div>
            <p className="text-2xl font-bold">{summary.clickToOpenRate.toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Tier Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Tier</CardTitle>
          <CardDescription>Compare performance across different re-engagement tiers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(tierBreakdown).map(([tier, stats]) => (
              <div key={tier} className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                <Badge className={tierLabels[tier]?.color || "bg-gray-100"}>
                  {tierLabels[tier]?.label || tier}
                </Badge>
                <div className="flex-1 grid grid-cols-3 md:grid-cols-6 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Sent</p>
                    <p className="font-semibold">{stats.totalSent}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Opened</p>
                    <p className="font-semibold">{stats.totalOpened}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Clicked</p>
                    <p className="font-semibold">{stats.totalClicked}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Open Rate</p>
                    <p className="font-semibold text-green-600">{stats.openRate.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Click Rate</p>
                    <p className="font-semibold text-blue-600">{stats.clickRate.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">CTO Rate</p>
                    <p className="font-semibold text-purple-600">{stats.clickToOpenRate.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Email Activity</CardTitle>
          <CardDescription>Last 20 re-engagement emails sent</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No re-engagement emails sent yet
            </p>
          ) : (
            <div className="space-y-2">
              {logs.slice(0, 20).map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Badge className={tierLabels[log.email_type]?.color || "bg-gray-100"}>
                      {tierLabels[log.email_type]?.label || log.email_type}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(log.sent_at).toLocaleDateString()} at{" "}
                      {new Date(log.sent_at).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Eye className={`h-4 w-4 ${log.opened_at ? "text-green-500" : "text-muted-foreground"}`} />
                      <span className="text-xs">{log.open_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointer className={`h-4 w-4 ${log.clicked_at ? "text-blue-500" : "text-muted-foreground"}`} />
                      <span className="text-xs">{log.click_count || 0}</span>
                    </div>
                    <Badge variant={log.status === "sent" ? "default" : "destructive"}>
                      {log.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
